// BM-H3851. Letter of Authority status on the matter page, above the AML
// panel because AML waits for it. Renders nothing on a matter where the
// client named nobody else to instruct us.
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { API } from "../config/api";
import { errorMessage } from "../utils/errorMessage";

const when = (iso) => (iso ? new Date(iso).toLocaleString("en-GB") : "");

export default function LoaStatusPanel({ matterRef, apiBase = API }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!matterRef) return;
    try {
      const res = await axios.get(`${apiBase}/loa/matter/${encodeURIComponent(matterRef)}`);
      setData(res.data);
    } catch {
      setData(null);
    }
  }, [matterRef, apiBase]);

  useEffect(() => { load(); }, [load]);

  if (!data || !data.needed) return null;

  const resend = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${apiBase}/loa/matter/${encodeURIComponent(matterRef)}/resend`);
      const to = (res.data?.resent_to || []).join(", ");
      toast.success(to ? `Signing link sent again to ${to}.` : "Nothing was outstanding to send.");
      await load();
    } catch (e) {
      toast.error(errorMessage(e, "Could not resend the Letter of Authority."));
    } finally {
      setBusy(false);
    }
  };

  const outstanding = data.outstanding || [];
  return (
    <section id="letter-of-authority" className="mb-5 p-4 bg-[rgba(26,37,71,0.7)] rounded-md border border-navy-border" data-testid="loa-status-panel">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-txt">Letter of Authority</h3>
        {outstanding.length > 0 && (
          <Button size="sm" variant="outline" onClick={resend} disabled={busy} data-testid="loa-resend">
            Resend signing link
          </Button>
        )}
      </div>
      {outstanding.length > 0 ? (
        <p className="text-xs text-status-amber mb-2" data-testid="loa-outstanding">
          AML cannot start until this is signed: {outstanding.join("; ")}.
        </p>
      ) : (
        <p className="text-xs text-status-green mb-2">Signed by everyone.</p>
      )}
      {(data.letters || []).map(l => (
        <div key={l.letter_id} className="text-xs text-txt-muted space-y-0.5 mb-2">
          <div className="text-txt">Authorised person: {l.authorised_person}</div>
          {l.signers.map(s => (
            <div key={s.role}>
              {s.role === "client" ? "Client" : "Authorised person"} {s.name}:{" "}
              {s.signed_at ? `signed ${when(s.signed_at)}`
                : s.invited_at ? `link sent ${when(s.invited_at)}, not signed`
                : s.invite_error ? `link not sent (${s.invite_error})`
                : "waiting"}
            </div>
          ))}
          {l.status === "complete" && (
            <div>
              {l.filed ? (l.filed_url ? <a href={l.filed_url} target="_blank" rel="noreferrer" className="underline">Filed in Compliance and Financials</a> : "Filed in Compliance and Financials")
                : `Not filed: ${l.file_error || "unknown error"}`}
              {l.copies_sent_to?.length ? `. Copies sent to ${l.copies_sent_to.join(", ")}.` : ". No copies sent."}
            </div>
          )}
        </div>
      ))}
    </section>
  );
}
